import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookRouteSurvey = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    pickup_address: '',
    delivery_address: '',
    intended_route: '',
    height_loaded: '',
    width: '',
    total_combination_length: '',
    weight: '',
    rear_overhang: '',
    trailer_type: '',
    steerable_axles: '',
    load_description: '',
    trailer_lowered: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const required = ['name', 'email', 'phone', 'pickup_address', 'delivery_address', 'intended_route', 'height_loaded', 'width', 'total_combination_length', 'weight', 'rear_overhang', 'trailer_type', 'steerable_axles', 'load_description', 'trailer_lowered'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/route-survey-bookings`, formData);
      setSubmitted(true);
      toast.success('Route survey request submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
          <Card className="max-w-md w-full mx-4 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Request Submitted</h2>
            <p className="text-gray-600 mb-6">Your route survey request has been received. Our team will review the details and be in touch shortly.</p>
            <Button onClick={() => navigate('/')} className="bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white">Back to Home</Button>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <h1 className="text-3xl font-bold text-[#1e3a5f] text-center mb-2">Book a Route Survey</h1>
          <p className="text-gray-600 text-center mb-8">Complete the form below and our team will assess your route requirements.</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Your Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input name="name" value={formData.name} onChange={handleChange} required placeholder="Full name" data-testid="survey-name" />
                </div>
                <div>
                  <Label>Company Name</Label>
                  <Input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Company (optional)" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" data-testid="survey-email" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} required placeholder="04XX XXX XXX" data-testid="survey-phone" />
                </div>
              </div>
            </Card>

            {/* Route Details */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Route Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Pick Up Location Address *</Label>
                  <Input name="pickup_address" value={formData.pickup_address} onChange={handleChange} required placeholder="Full pickup address" data-testid="survey-pickup" />
                </div>
                <div>
                  <Label>Delivery Location Address *</Label>
                  <Input name="delivery_address" value={formData.delivery_address} onChange={handleChange} required placeholder="Full delivery address" data-testid="survey-delivery" />
                </div>
              </div>
              <div className="mt-4">
                <Label>Intended Route *</Label>
                <Textarea
                  name="intended_route"
                  value={formData.intended_route}
                  onChange={handleChange}
                  required
                  placeholder="Describe the intended route in detail — include road names, highways, turns, intersections, bridges, and any known restrictions..."
                  rows={10}
                  maxLength={100000}
                  className="min-h-[200px]"
                  data-testid="survey-route"
                />
                <p className="text-xs text-gray-400 mt-1">{formData.intended_route.length.toLocaleString()} / 100,000 characters</p>
              </div>
            </Card>

            {/* Load & Vehicle Specs */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Load & Vehicle Specifications</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Height Loaded *</Label>
                  <Input name="height_loaded" value={formData.height_loaded} onChange={handleChange} required placeholder="e.g. 4.6m" data-testid="survey-height" />
                </div>
                <div>
                  <Label>Width *</Label>
                  <Input name="width" value={formData.width} onChange={handleChange} required placeholder="e.g. 3.5m" data-testid="survey-width" />
                </div>
                <div>
                  <Label>Total Combination Length *</Label>
                  <Input name="total_combination_length" value={formData.total_combination_length} onChange={handleChange} required placeholder="Prime mover + dolly + trailer/s + overhang" data-testid="survey-length" />
                  <p className="text-xs text-gray-400 mt-1">Include prime mover, dolly, trailer/s and overhang</p>
                </div>
                <div>
                  <Label>Weight *</Label>
                  <Input name="weight" value={formData.weight} onChange={handleChange} required placeholder="e.g. 45 tonnes" data-testid="survey-weight" />
                </div>
                <div>
                  <Label>Rear Overhang *</Label>
                  <Input name="rear_overhang" value={formData.rear_overhang} onChange={handleChange} required placeholder="e.g. 2.5m" data-testid="survey-overhang" />
                </div>
                <div>
                  <Label>Type of Trailer *</Label>
                  <Input name="trailer_type" value={formData.trailer_type} onChange={handleChange} required placeholder="e.g. Low loader, flat top, extendable" data-testid="survey-trailer" />
                </div>
              </div>
              <div className="mt-4">
                <Label>Steerable Axles *</Label>
                <Textarea
                  name="steerable_axles"
                  value={formData.steerable_axles}
                  onChange={handleChange}
                  required
                  placeholder="Describe steerable axle configuration..."
                  rows={2}
                  maxLength={300}
                  data-testid="survey-axles"
                />
                <p className="text-xs text-gray-400 mt-1">{formData.steerable_axles.length} / 300 characters</p>
              </div>
              <div className="mt-4">
                <Label>Load Description *</Label>
                <Textarea
                  name="load_description"
                  value={formData.load_description}
                  onChange={handleChange}
                  required
                  placeholder="Describe the load being transported..."
                  rows={3}
                  data-testid="survey-load-desc"
                />
              </div>
              <div className="mt-4">
                <Label>Can the Trailer Be Lowered? If So, How Low? *</Label>
                <Textarea
                  name="trailer_lowered"
                  value={formData.trailer_lowered}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Yes, can be lowered to 600mm deck height / No, fixed height"
                  rows={2}
                  data-testid="survey-lowered"
                />
              </div>
            </Card>

            {/* Additional Message */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-[#1e3a5f] mb-4 border-b pb-2">Additional Information</h2>
              <div>
                <Label>Message for Admin</Label>
                <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Any additional information or special requirements..." rows={4} />
              </div>
            </Card>

            <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6" data-testid="submit-route-survey">
              {loading ? 'Submitting...' : 'Submit Route Survey Request'}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BookRouteSurvey;
